import { FC, ReactElement, useEffect, useMemo, useState } from 'react';

import { useDialog } from 'hooks/useDialog';

import { RaaExpandablePreview } from 'raa-react';

import { DrawerSection } from 'components/drawerSections/DrawerSections';
import { TextButton } from 'components/sharedElements/sharedElements';
import UpphovsmanInfoDialog from 'components/utkast/upphovsmanInfoDialog/UpphovsmanInfoDialog';

import { BebyggelseobjektDomaner, Handelse, Upphovsmaninsats, Upphovsmanroll } from 'models';

import { renderWithParagraphs } from 'utilities/renderWithParagraphs';
import { sortHandelser } from 'utilities/sortUtility';

interface BebyggelseHistorikSectionProps {
  historik: string;
  handelser?: Array<Handelse>;
  domaner: BebyggelseobjektDomaner | undefined;
}

const BebyggelseHistorikSection: FC<BebyggelseHistorikSectionProps> = (
  props: BebyggelseHistorikSectionProps
): ReactElement => {
  const { setDialog, unsetDialog } = useDialog();
  const [handelser, setHandelser] = useState<Array<Handelse>>([]);

  const getHandelseTitle = (handelse: Handelse): string => {
    let date = '';
    if (!handelse.datering_kand) {
      date = 'okänt årtal';
    } else if (
      (handelse.datering_from && !handelse.datering_tom) ||
      (handelse.datering_from && handelse.datering_from === handelse.datering_tom)
    ) {
      date = `klar ${handelse.datering_from}`;
    } else if (handelse.datering_from && handelse.datering_tom) {
      date = `klar ca ${handelse.datering_from}–${handelse.datering_tom}`;
    }

    return `${handelse.handelsetyp_namn}, ${date}`;
  };

  useEffect(() => {
    if (props.handelser) {
      setHandelser(sortHandelser([...props.handelser], props.domaner?.handelsetyper) || []);
    }
  }, [props.handelser, props.domaner]);

  // Extrahera content här istället för att skriva ut den två gånger i JSX:en
  const content = useMemo(() => {
    return (
      <>
        {renderWithParagraphs(props.historik)}
        <ul className="tw-flex tw-flex-col tw-gap-4">
          {handelser?.map((handelse: Handelse, index: number) => {
            return (
              <li key={index}>
                <h3 className="tw-font-semibold">{getHandelseTitle(handelse)}</h3>
                <p>{handelse.kommentar}</p>
                <ul>
                  {handelse.upphovsmanroller.map((upphovsman: Upphovsmaninsats) => {
                    return upphovsman.roller.map((roll: Upphovsmanroll, i: number) => {
                      return (
                        <li className="raa-secondary-text tw-flex" key={i}>
                          <span className="tw-mr-2">{`${roll.roll_namn}:`}</span>
                          <TextButton
                            onClick={() =>
                              setDialog(UpphovsmanInfoDialog, {
                                unsetDialog,
                                id: upphovsman.upphovsman.upphovsman_id
                              })
                            }
                          >
                            <span className="tw-mr-2 tw-text-raa-orange">
                              {'fornamn' in upphovsman.upphovsman
                                ? `${upphovsman.upphovsman.fornamn} ${upphovsman.upphovsman.efternamn}`
                                : `${upphovsman.upphovsman.upphovsorganisationsnamn}`}
                            </span>
                          </TextButton>
                        </li>
                      );
                    });
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      </>
    );
  }, [handelser, props.historik, setDialog, unsetDialog]);

  return (
    <DrawerSection>
      <h2 className="tw-heading-md tw-mb-4">Historik</h2>
      <RaaExpandablePreview ariaLabel="Historik av bebyggelse">{content}</RaaExpandablePreview>
    </DrawerSection>
  );
};

export default BebyggelseHistorikSection;
